/**
 * Planned TSS / IF / duration / distance from structured workout JSON.
 * Mirrors backend PlannedWorkoutMetricsService and mobile plannedWorkoutMetrics.ts.
 */

import type { ApiWorkoutStructure } from './workoutStructure';
import { isOpenDurationStep } from './workoutStructure';
import type { PrimaryIntensityMetric } from './workoutBuilderSettings';

export type PlannedMetricsResult = {
  total_time_planned: number;
  distance_planned?: number;
  if_planned?: number;
  tss_planned?: number;
};

function resolveIntensityFraction(
  target: { minValue?: number | string; maxValue?: number | string } | undefined,
): number | null {
  const max = target?.maxValue != null ? Number(target.maxValue) : NaN;
  const min = target?.minValue != null ? Number(target.minValue) : max;
  if (!Number.isFinite(max) || max <= 0) {
    return null;
  }
  const midpoint = (min + max) / 2;
  if (!Number.isFinite(midpoint) || midpoint <= 0) {
    return null;
  }
  if (max > 150 || min > 150) {
    return null;
  }
  return midpoint / 100;
}

function resolveStepSeconds(
  step: {
    openDuration?: boolean | string;
    length?: { unit?: string; value?: number | string };
    targets?: { minValue?: number | string; maxValue?: number | string }[];
  },
  thresholdSpeedMps: number,
): { seconds: number; meters: number; intensityFraction: number | null } {
  if (isOpenDurationStep(step.openDuration)) {
    return {
      seconds: 0,
      meters: 0,
      intensityFraction: resolveIntensityFraction(step.targets?.[0]),
    };
  }

  const unit = step.length?.unit;
  const raw = Number(step.length?.value) || 0;
  let seconds = 0;
  let meters = 0;

  if (unit === 'second') {
    seconds = Math.round(raw);
  } else if (unit === 'minute') {
    seconds = Math.round(raw * 60);
  } else if (unit === 'meter') {
    meters = Math.round(raw);
  }

  const intensityFraction = resolveIntensityFraction(step.targets?.[0]);

  if (meters > 0 && seconds <= 0 && thresholdSpeedMps > 0) {
    const fraction = intensityFraction ?? 1;
    if (fraction > 0) {
      seconds = Math.max(1, Math.round(meters / (thresholdSpeedMps * fraction)));
    }
  }

  return {
    seconds: Math.max(0, seconds),
    meters: Math.max(0, meters),
    intensityFraction,
  };
}

function canCalculateLoadMetrics(
  metric: PrimaryIntensityMetric,
  ftp: number,
  thresholdSpeedMps: number,
  ltHr: number,
  maxHr: number,
): boolean {
  switch (metric) {
    case 'percentOfFtp':
      return ftp > 0;
    case 'percentOfThresholdSpeed':
      return thresholdSpeedMps > 0;
    case 'percentOfLTHR':
      return ltHr > 0;
    case 'percentOfMaxHr':
      return maxHr > 0;
    default:
      return false;
  }
}

export function calculatePlannedMetricsFromBlocks(
  blocks: { type?: string; length?: { value?: number | string }; steps?: unknown[] }[],
  options: {
    primaryIntensityMetric: PrimaryIntensityMetric;
    primaryIntensityTargetOrRange?: 'range' | 'single';
    ftp?: number;
    ltHr?: number;
    maxHr?: number;
    thresholdSpeedMps?: number;
  },
): PlannedMetricsResult {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return { total_time_planned: 0 };
  }

  const {
    primaryIntensityMetric,
    ftp = 0,
    ltHr = 0,
    maxHr = 0,
    thresholdSpeedMps = 0,
  } = options;

  let totalTimeSeconds = 0;
  let totalDistanceMeters = 0;
  let weightedIntensitySum = 0;

  blocks.forEach(block => {
    if (!block?.type) {
      return;
    }

    const blockType = String(block.type).toLowerCase();
    const repeatCount =
      blockType === 'repetition' || blockType === 'repeat'
        ? Math.max(1, Number(block.length?.value) || 1)
        : 1;
    const steps = block.steps;
    if (!Array.isArray(steps) || steps.length === 0) {
      return;
    }

    let blockTimeSeconds = 0;
    let blockDistanceMeters = 0;
    let blockIntensityWeight = 0;

    steps.forEach((rawStep: unknown) => {
      const step = rawStep as Parameters<typeof resolveStepSeconds>[0];
      const { seconds, meters, intensityFraction } = resolveStepSeconds(step, thresholdSpeedMps);
      if (seconds <= 0 && meters <= 0) {
        return;
      }

      blockTimeSeconds += seconds;
      blockDistanceMeters += meters;

      if (intensityFraction != null && seconds > 0) {
        blockIntensityWeight += seconds * intensityFraction;
      }
    });

    if (blockTimeSeconds <= 0 && blockDistanceMeters <= 0) {
      return;
    }

    totalTimeSeconds += blockTimeSeconds * repeatCount;
    totalDistanceMeters += blockDistanceMeters * repeatCount;
    weightedIntensitySum += blockIntensityWeight * repeatCount;
  });

  const result: PlannedMetricsResult = {
    total_time_planned: totalTimeSeconds,
  };

  if (totalDistanceMeters > 0) {
    result.distance_planned = totalDistanceMeters;
  }

  if (
    totalTimeSeconds <= 0 ||
    weightedIntensitySum <= 0 ||
    !canCalculateLoadMetrics(primaryIntensityMetric, ftp, thresholdSpeedMps, ltHr, maxHr)
  ) {
    return result;
  }

  const ifPlanned = weightedIntensitySum / totalTimeSeconds;
  result.if_planned = Math.round(ifPlanned * 100) / 100;
  result.tss_planned = Math.round((totalTimeSeconds * ifPlanned * ifPlanned) / 36);

  return result;
}

export function calculatePlannedMetricsFromStructure(
  structure: ApiWorkoutStructure | { structure?: unknown[]; primaryIntensityMetric?: string },
  options: {
    primaryIntensityMetric?: PrimaryIntensityMetric;
    ftp?: number;
    ltHr?: number;
    maxHr?: number;
    thresholdSpeedMps?: number;
  },
): PlannedMetricsResult {
  const blocks = (structure as ApiWorkoutStructure).structure ?? [];
  const metric =
    ((structure as ApiWorkoutStructure).primaryIntensityMetric as PrimaryIntensityMetric) ||
    options.primaryIntensityMetric ||
    'percentOfFtp';

  return calculatePlannedMetricsFromBlocks(blocks as Parameters<typeof calculatePlannedMetricsFromBlocks>[0], {
    ...options,
    primaryIntensityMetric: metric,
    primaryIntensityTargetOrRange:
      (structure as ApiWorkoutStructure).primaryIntensityTargetOrRange as 'range' | 'single' | undefined,
  });
}
